/**
 * AlumierMD Patient Verification — Add to Cart Gate
 *
 * Site-wide gate that blocks Add to Cart for AlumierMD products.
 * Source of truth: window.__alumierVariantIds (emitted by the Liquid snippet
 * from the /collections/alumiermd smart collection).
 *
 * Flow: Click Add to Cart → Check variant ID → If AlumierMD, block + show modal
 *       → Verify email via App Proxy → Allow on success.
 */
(function () {
  'use strict';

  var APP_PROXY_BASE = 'https://alumiermd-shopify-integration-serve.vercel.app/proxy';

  // ---------------------
  // Language detection
  // ---------------------
  var isFrench = /^\/(fr)(\/|$)/.test(window.location.pathname);

  var strings = isFrench ? {
    verify: 'Vérifier',
    verified: 'Merci ! Votre statut de patient AlumierMD est confirmé. Vous pouvez maintenant poursuivre votre achat.',
    notFound: "Votre courriel n'est pas enregistré auprès d'AlumierMD. Veuillez d'abord vous inscrire comme patient pour acheter ces produits.",
    registerHeading: 'Inscrivez-vous pour continuer :',
    firstName: 'Prénom',
    lastName: 'Nom',
    registerBtn: "S'inscrire et continuer",
    notOwned: "Ce courriel est associé à un autre fournisseur AlumierMD. Veuillez utiliser un autre courriel pour vous inscrire.",
    tryAnother: 'Essayez un autre courriel :',
    registerSuccess: 'Merci pour votre inscription ! Vous pouvez maintenant ajouter des produits AlumierMD à votre panier.',
    suspiciousEmail: 'Est-ce le bon courriel ?',
    confirmYes: 'Oui, continuer',
    confirmNo: 'Non, modifier',
    fallback: 'Vous pouvez poursuivre votre achat.',
    verifying: 'Vérification de votre statut de patient AlumierMD en cours...',
    registering: "Inscription en cours..."
  } : {
    verify: 'Verify',
    verified: 'Thank you! Your AlumierMD patient status is confirmed. You can now proceed with your purchase.',
    notFound: 'Your email is not registered with AlumierMD. Please register as a patient first to purchase these products.',
    registerHeading: 'Register to continue:',
    firstName: 'First Name',
    lastName: 'Last Name',
    registerBtn: 'Register & Continue',
    notOwned: 'This email is associated with a different AlumierMD provider. Please use another email to register.',
    tryAnother: 'Try another email:',
    registerSuccess: 'Thank you for registering! You can now add AlumierMD products to your cart.',
    suspiciousEmail: 'Is this the correct email?',
    confirmYes: 'Yes, continue',
    confirmNo: 'No, change it',
    fallback: 'You can proceed with your purchase.',
    verifying: 'Verifying your AlumierMD patient status...',
    registering: 'Registering your account...'
  };

  // Verification state — restored from sessionStorage
  var verifiedEmail = null;
  var verificationStatus = null;

  try {
    var saved = sessionStorage.getItem('alumier_verified');
    if (saved) {
      var parsed = JSON.parse(saved);
      verifiedEmail = parsed.email || null;
      verificationStatus = parsed.status || null;
    }
  } catch (e) {}

  function saveVerification(email, status) {
    verifiedEmail = email;
    verificationStatus = status;
    try {
      sessionStorage.setItem('alumier_verified', JSON.stringify({ email: email, status: status }));
    } catch (e) {}
  }

  function clearVerification() {
    verifiedEmail = null;
    verificationStatus = null;
    try { sessionStorage.removeItem('alumier_verified'); } catch (e) {}
  }

  function isVerified() {
    return verificationStatus === 'allowed' && !!verifiedEmail;
  }

  // -------------------------------------------------------
  // AlumierMD variant lookup — source of truth is window.__alumierVariantIds
  // -------------------------------------------------------
  var alumierVariantSet = null;

  function getAlumierVariantSet() {
    if (alumierVariantSet) return alumierVariantSet;
    var list = window.__alumierVariantIds || [];
    var set = {};
    for (var i = 0; i < list.length; i++) {
      var id = String(list[i]).trim();
      if (id) set[id] = true;
    }
    alumierVariantSet = set;
    return set;
  }

  function isAlumierVariantId(id) {
    if (id === undefined || id === null || id === '') return false;
    var set = getAlumierVariantSet();
    return !!set[String(id).trim()];
  }

  // Extract variant id from any supported cart/add payload
  function extractVariantId(body) {
    if (!body) return null;

    // FormData
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return body.get('id');
    }

    // URLSearchParams
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body.get('id');
    }

    // JSON string
    if (typeof body === 'string') {
      var trimmed = body.trim();
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
        try {
          var parsed = JSON.parse(trimmed);
          if (parsed && parsed.id) return parsed.id;
          if (parsed && Array.isArray(parsed.items) && parsed.items[0]) return parsed.items[0].id;
        } catch (e) {}
      }
      // URL-encoded string
      try {
        var params = new URLSearchParams(trimmed);
        var id = params.get('id');
        if (id) return id;
      } catch (e) {}
    }

    // Plain object
    if (typeof body === 'object') {
      if (body.id) return body.id;
      if (Array.isArray(body.items) && body.items[0]) return body.items[0].id;
    }

    return null;
  }

  function payloadHasAlumierVariant(body) {
    var id = extractVariantId(body);
    if (id && isAlumierVariantId(id)) return true;

    // Handle multi-item adds
    if (body && typeof body === 'object' && !(body instanceof FormData) && !(typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
      if (Array.isArray(body.items)) {
        for (var i = 0; i < body.items.length; i++) {
          if (isAlumierVariantId(body.items[i].id)) return true;
        }
      }
    }
    return false;
  }

  // -------------------------------------------------------
  // DOM helpers
  // -------------------------------------------------------
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  function getModal() {
    return document.getElementById('alumier-modal');
  }

  // Status target priority:
  //   1. Dynamically injected inline gate (next to clicked Add-to-Cart form)
  //   2. Product-page static banner (rendered by snippet on product page)
  //   3. In-modal status area (fallback for checkout backstop)
  function getStatusEl() {
    return document.getElementById('alumier-inline-status')
        || document.getElementById('alumier-product-status')
        || document.getElementById('alumier-gate-status');
  }

  function statusIsInline() {
    return !!(document.getElementById('alumier-inline-status')
         || document.getElementById('alumier-product-status'));
  }

  function showModal() {
    var modal = getModal();
    if (!modal) return;
    // Escape any parent stacking context (cart drawer, etc.) by moving the
    // modal to <body>. This makes our max z-index actually top-of-page.
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
    modal.style.display = 'flex';

    // Disable all sibling top-level elements (incl. the theme's Quick View
    // dialog) so their focus traps can't steal focus from our email input.
    for (var i = 0; i < document.body.children.length; i++) {
      var child = document.body.children[i];
      if (child !== modal) child.setAttribute('inert', '');
    }

    // Focus the email input so the user can type immediately
    setTimeout(function () {
      var input = modal.querySelector('input[type="email"]');
      if (input && typeof input.focus === 'function') input.focus();
    }, 50);
  }

  function hideModal() {
    var modal = getModal();
    if (modal) modal.style.display = 'none';
    // Re-enable everything else
    for (var i = 0; i < document.body.children.length; i++) {
      document.body.children[i].removeAttribute('inert');
    }
  }

  function getIntroEl() {
    // Prefer inline gate's intro if injected; else modal's intro.
    return document.getElementById('alumier-inline-intro')
        || document.getElementById('alumier-modal-intro');
  }

  function hideIntro() {
    var el = getIntroEl();
    if (el) el.style.display = 'none';
  }

  function showIntro() {
    var el = getIntroEl();
    if (el) el.style.display = 'block';
  }

  // -------------------------------------------------------
  // Inline gate — injected next to the clicked Add-to-Cart form
  // Replaces the modal for all add-to-cart contexts (product page,
  // Quick View, collection cards). Modal stays as checkout backstop.
  // -------------------------------------------------------
  var lastCartAddForm = null;

  // Track whichever cart/add form the user most recently interacted with,
  // so we know where to inject the inline gate when the gate fires.
  document.addEventListener('click', function (e) {
    var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
    if (form) lastCartAddForm = form;
  }, true);

  function removeInlineGate() {
    var existing = document.getElementById('alumier-inline-gate');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  function buildInlineGateHTML() {
    var hasCustomer = !!window.__alumierCustomerEmail;
    var title = isFrench ? 'Vérification du patient AlumierMD' : 'AlumierMD Patient Verification';
    var intro = isFrench
      ? 'Les produits AlumierMD sont exclusivement réservés aux patients inscrits. Veuillez vérifier votre courriel pour continuer.'
      : 'AlumierMD products are available exclusively to registered patients. Please verify your email to continue.';
    var emailLabel = isFrench ? 'Votre courriel :' : 'Your email address:';
    var emailPlaceholder = isFrench ? 'votre@courriel.com' : 'your@email.com';

    // Intro gets its own frame; container is a transparent positioned wrapper
    // so the close button can float over whichever section is visible.
    var introStyle = 'padding:20px;border:1px solid #d0cfc8;border-radius:8px;background:#fafaf8;';
    var html = '<button type="button" class="alumier-inline-close" aria-label="Close">&times;</button>';
    html += '<div id="alumier-inline-intro" style="' + introStyle + '">';
    html +=   '<h4 style="margin:0 0 8px;font-size:16px;padding-right:28px;">' + title + '</h4>';
    html +=   '<p style="margin:0 0 12px;font-size:14px;color:#555;">' + intro + '</p>';
    if (!hasCustomer) {
      html += '<form id="alumier-inline-form">';
      html +=   '<label for="alumier-inline-email" style="display:block;font-size:13px;margin-bottom:4px;">' + emailLabel + '</label>';
      html +=   '<input type="email" id="alumier-inline-email" name="email" required placeholder="' + emailPlaceholder + '" class="alumier-input" />';
      html +=   '<button type="submit" class="alumier-btn">' + strings.verify + '</button>';
      html += '</form>';
    }
    html += '</div>';
    html += '<div id="alumier-inline-status" class="alumier-banner" style="display:none;padding-right:40px;"></div>';
    return html;
  }

  function injectInlineGate(afterForm) {
    removeInlineGate();
    var container = document.createElement('div');
    container.id = 'alumier-inline-gate';
    container.style.cssText = 'margin:16px 0;position:relative;';
    container.innerHTML = buildInlineGateHTML();
    // Inject right after the form so it appears under the Add to Cart button
    if (afterForm.nextSibling) {
      afterForm.parentNode.insertBefore(container, afterForm.nextSibling);
    } else {
      afterForm.parentNode.appendChild(container);
    }

    // Close button style + handler (lives inside the intro frame)
    var closeBtn = container.querySelector('.alumier-inline-close');
    if (closeBtn) {
      closeBtn.style.cssText = 'position:absolute;top:8px;right:12px;background:none;border:none;font-size:22px;cursor:pointer;color:#666;padding:0;width:28px;height:28px;line-height:1;';
      closeBtn.addEventListener('click', removeInlineGate);
    }

    // Scroll into view + focus email input
    setTimeout(function () {
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var input = container.querySelector('input[type="email"]');
      if (input && typeof input.focus === 'function') input.focus();
    }, 50);

    return container;
  }

  function showStatus(type, html) {
    var el = getStatusEl();
    if (!el) return;

    if (statusIsInline()) {
      // Inline target (injected gate or product-page banner) — close modal
      hideModal();
      hideIntro();
      el.className = 'alumier-banner alumier-banner--' + type;
      el.innerHTML = html;
      el.style.display = 'block';
      setTimeout(function () {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      // No inline target — open modal and render status inside it
      showModal();
      hideIntro();
      el.className = 'alumier-banner alumier-banner--' + type;
      el.innerHTML = html;
      el.style.display = 'block';
    }
  }

  function hideStatus() {
    var el = getStatusEl();
    if (el) el.style.display = 'none';
    if (!statusIsInline()) showIntro();
  }

  // -------------------------------------------------------
  // Verification API call
  // -------------------------------------------------------
  var commonTlds = ['com','ca','net','org','edu','gov','co','io','me','info','biz','us','uk','au','de','fr','it','es','nl','be','ch','at','ru','jp','cn','in','br','mx','za','nz','se','no','dk','fi','pl','pt','ie','cz','ro','hu','gr','il','ae','sg','hk','tw','kr','co.uk','com.au','co.nz','com.br','co.za','co.in'];

  function isSuspiciousEmail(email) {
    var parts = email.split('@');
    if (parts.length !== 2) return true;
    var domain = parts[1].toLowerCase();
    var tld = domain.substring(domain.lastIndexOf('.') + 1);
    var domainParts = domain.split('.');
    var fullTld = domainParts.length > 2 ? domainParts.slice(-2).join('.') : tld;
    if (commonTlds.indexOf(tld) === -1 && commonTlds.indexOf(fullTld) === -1) return true;
    var domainName = domainParts[0];
    if (/[^aeiou]{6,}/i.test(domainName)) return true;
    return false;
  }

  var suspiciousConfirmed = {};

  function verifyEmail(email, callback) {
    callback = callback || function () {};

    if (isSuspiciousEmail(email) && !suspiciousConfirmed[email]) {
      showStatus('warning',
        '<p>&#9888; ' + strings.suspiciousEmail + ' <strong>' + escapeHtml(email) + '</strong></p>' +
        '<p><button id="alumier-confirm-yes" class="alumier-btn">' + strings.confirmYes + '</button> ' +
        '<button id="alumier-confirm-no" class="alumier-btn" style="background:#e0cccc;color:#6b3333;border:1px solid #c09090;">' + strings.confirmNo + '</button></p>');
      setTimeout(function () {
        var yesBtn = document.getElementById('alumier-confirm-yes');
        var noBtn = document.getElementById('alumier-confirm-no');
        if (yesBtn) {
          yesBtn.addEventListener('click', function () {
            suspiciousConfirmed[email] = true;
            verifyEmail(email, callback);
          });
        }
        if (noBtn) {
          noBtn.addEventListener('click', function () {
            hideStatus();
          });
        }
      }, 50);
      return;
    }

    showStatus('info', '<p><span class="alumier-spinner"></span> ' + strings.verifying + '</p>');

    fetch(APP_PROXY_BASE + '/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.error) {
          clearVerification();
          var errHtml = '<p>' + escapeHtml(data.error) + '</p>' +
            '<div style="margin-top:12px;">' +
            '<form id="alumier-retry-form">' +
            '<input type="email" id="alumier-retry-email" name="email" required placeholder="your@email.com" class="alumier-input" />' +
            '<button type="submit" class="alumier-btn">' + strings.verify + '</button>' +
            '</form></div>';
          showStatus('error', errHtml);
          setTimeout(function () {
            var form = document.getElementById('alumier-retry-form');
            if (form) {
              form.addEventListener('submit', function (e) {
                e.preventDefault();
                var newEmail = document.getElementById('alumier-retry-email').value;
                if (newEmail) verifyEmail(newEmail.trim(), callback);
              });
            }
          }, 100);
          callback(false, data);
          return;
        }

        if (data.allowed) {
          saveVerification(email.trim().toLowerCase(), 'allowed');
          showStatus('success', '<p>' + strings.verified + '</p>');
          callback(true, data);
        } else if (data.reason === 'not_found') {
          verificationStatus = 'not_found';
          var html = '<p>' + strings.notFound + '</p>';
          html += '<div id="alumier-register-inline" style="margin-top:12px;">' +
            '<p><strong>' + strings.registerHeading + '</strong></p>' +
            '<form id="alumier-register-form">' +
            '<input type="text" name="firstName" placeholder="' + strings.firstName + '" required class="alumier-input" />' +
            '<input type="text" name="lastName" placeholder="' + strings.lastName + '" required class="alumier-input" />' +
            '<input type="email" name="email" value="' + escapeHtml(email) + '" class="alumier-input" />' +
            '<button type="submit" class="alumier-btn">' + strings.registerBtn + '</button>' +
            '</form></div>';
          showStatus('warning', html);
          attachRegisterHandler(email);
          callback(false, data);
        } else if (data.reason === 'not_owned') {
          verificationStatus = 'not_owned';
          var errorHtml = '<p>' + strings.notOwned + '</p>' +
            '<div style="margin-top:12px;">' +
            '<form id="alumier-retry-form">' +
            '<label for="alumier-retry-email">' + strings.tryAnother + '</label>' +
            '<input type="email" id="alumier-retry-email" name="email" required placeholder="your@email.com" class="alumier-input" />' +
            '<button type="submit" class="alumier-btn">' + strings.verify + '</button>' +
            '</form></div>';
          showStatus('error', errorHtml);
          setTimeout(function () {
            var form = document.getElementById('alumier-retry-form');
            if (form) {
              form.addEventListener('submit', function (e) {
                e.preventDefault();
                var newEmail = document.getElementById('alumier-retry-email').value;
                if (newEmail) verifyEmail(newEmail.trim(), callback);
              });
            }
          }, 100);
          callback(false, data);
        } else {
          showStatus('error', '<p>Unexpected response. Please try again.</p>');
          callback(false, data);
        }
      })
      .catch(function () {
        // API down — fail open so customers are not blocked
        saveVerification(email.trim().toLowerCase(), 'allowed');
        showStatus('success', '<p>' + strings.fallback + '</p>');
        callback(true, null);
      });
  }

  // -------------------------------------------------------
  // Registration
  // -------------------------------------------------------
  function attachRegisterHandler(email) {
    setTimeout(function () {
      var form = document.getElementById('alumier-register-form');
      if (!form) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var fd = new FormData(form);
        var regEmail = (fd.get('email') || email).trim();
        var regData = {
          email: regEmail,
          firstName: fd.get('firstName'),
          lastName: fd.get('lastName'),
        };

        if (isSuspiciousEmail(regEmail) && !suspiciousConfirmed[regEmail]) {
          showStatus('warning',
            '<p>&#9888; ' + strings.suspiciousEmail + ' <strong>' + escapeHtml(regEmail) + '</strong></p>' +
            '<p><button id="alumier-confirm-yes" class="alumier-btn">' + strings.confirmYes + '</button> ' +
            '<button id="alumier-confirm-no" class="alumier-btn" style="background:#e0cccc;color:#6b3333;border:1px solid #c09090;">' + strings.confirmNo + '</button></p>');
          setTimeout(function () {
            var yesBtn = document.getElementById('alumier-confirm-yes');
            var noBtn = document.getElementById('alumier-confirm-no');
            if (yesBtn) {
              yesBtn.addEventListener('click', function () {
                suspiciousConfirmed[regEmail] = true;
                registerPatient(regData);
              });
            }
            if (noBtn) {
              noBtn.addEventListener('click', function () {
                verifyEmail(email, function () {});
              });
            }
          }, 50);
          return;
        }

        registerPatient(regData);
      });
    }, 100);
  }

  function registerPatient(data) {
    showStatus('info', '<p><span class="alumier-spinner"></span> ' + strings.registering + '</p>');

    fetch(APP_PROXY_BASE + '/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(function (res) { return res.json(); })
      .then(function (result) {
        if (result.error) {
          showStatus('error', '<p>' + escapeHtml(result.error) + '</p>');
          return;
        }
        if (result.success) {
          saveVerification(data.email.trim().toLowerCase(), 'allowed');
          showStatus('success', '<p>' + strings.registerSuccess + '</p>');
        } else {
          showStatus('error', '<p>' + escapeHtml(result.error || 'Registration failed.') + '</p>');
        }
      })
      .catch(function () {
        showStatus('error', '<p>Registration failed. Please try again.</p>');
      });
  }

  // -------------------------------------------------------
  // Prompt helper — prefers inline gate next to clicked form,
  // falls back to modal (checkout backstop when no form context).
  // Auto-verifies logged-in customer in either case.
  // -------------------------------------------------------
  function showVerificationPrompt() {
    var useInline = lastCartAddForm && document.body.contains(lastCartAddForm);
    if (useInline) {
      injectInlineGate(lastCartAddForm);
    } else {
      showModal();
    }
    var customerEmail = window.__alumierCustomerEmail;
    if (customerEmail && !verifiedEmail) {
      verifyEmail(customerEmail);
    }
  }

  // -------------------------------------------------------
  // Add to Cart interception (site-wide, pre-emptive by variant ID)
  // -------------------------------------------------------
  function interceptAddToCart() {
    // Form submissions to /cart/add
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.id === 'alumier-verify-form-modal' || form.id === 'alumier-inline-form' || form.id === 'alumier-register-form' || form.id === 'alumier-retry-form') return;

      var action = (form.getAttribute('action') || '').toLowerCase();
      if (action.indexOf('/cart/add') === -1) return;

      if (isVerified()) return;

      var fd;
      try { fd = new FormData(form); } catch (err) { return; }
      var variantId = fd.get('id');
      if (!isAlumierVariantId(variantId)) return;

      e.preventDefault();
      e.stopPropagation();
      showVerificationPrompt();
    }, true);

    // fetch-based cart adds
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var init = arguments[1];
      var urlStr = typeof url === 'string' ? url : (url && url.url) || '';

      if (urlStr.indexOf('/cart/add') !== -1 && !isVerified()) {
        var body = init && init.body;
        if (payloadHasAlumierVariant(body)) {
          showVerificationPrompt();
          return Promise.reject(new Error('AlumierMD verification required'));
        }
      }
      return originalFetch.apply(this, arguments);
    };

    // XHR-based cart adds
    var originalXhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._alumierUrl = url;
      return originalXhrOpen.apply(this, arguments);
    };

    var originalXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
      if (this._alumierUrl && String(this._alumierUrl).indexOf('/cart/add') !== -1 && !isVerified()) {
        if (payloadHasAlumierVariant(body)) {
          showVerificationPrompt();
          return; // abort silently
        }
      }
      return originalXhrSend.apply(this, arguments);
    };
  }

  // -------------------------------------------------------
  // Dynamic checkout buttons (Buy with Shop, Apple Pay, etc.)
  // Only blocks on product pages for an AlumierMD product.
  // -------------------------------------------------------
  function interceptDynamicCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.shopify-payment-button button, .shopify-payment-button a, [data-shopify="payment-button"], .dynamic-checkout__content button');
      if (!btn) return;

      // Find a variant id in the surrounding product form
      var form = btn.closest('form');
      var variantId = null;
      if (form) {
        try { variantId = new FormData(form).get('id'); } catch (err) {}
      }
      if (!variantId) return;
      if (!isAlumierVariantId(variantId)) return;
      if (isVerified()) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showVerificationPrompt();
    }, true);
  }

  // -------------------------------------------------------
  // Checkout backstop — block checkout if cart has an AlumierMD item
  // -------------------------------------------------------
  var cartHasAlumier = false;

  function refreshCartAlumierFlag() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartHasAlumier = false;
        if (cart && Array.isArray(cart.items)) {
          for (var i = 0; i < cart.items.length; i++) {
            if (isAlumierVariantId(cart.items[i].variant_id || cart.items[i].id)) {
              cartHasAlumier = true;
              break;
            }
          }
        }
        return cartHasAlumier;
      })
      .catch(function () { return false; });
  }

  function interceptCheckout() {
    // Checkout form submit
    document.addEventListener('submit', function (e) {
      var form = e.target;
      var action = (form.getAttribute('action') || '').toLowerCase();
      var hasCheckoutButton = form.querySelector('[name="checkout"]');
      if (action.indexOf('/checkout') === -1 && !hasCheckoutButton) return;
      if (!cartHasAlumier || isVerified()) return;
      e.preventDefault();
      e.stopPropagation();
      showVerificationPrompt();
    }, true);

    // Checkout button / link click
    document.addEventListener('click', function (e) {
      var hit = e.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      if (!hit) return;
      if (!cartHasAlumier || isVerified()) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showVerificationPrompt();
    }, true);
  }

  // -------------------------------------------------------
  // Initialize
  // -------------------------------------------------------
  function init() {
    interceptAddToCart();
    interceptDynamicCheckout();
    interceptCheckout();

    // Initial cart check + refresh on cart mutations
    refreshCartAlumierFlag();
    document.addEventListener('cart:updated', refreshCartAlumierFlag);
    document.addEventListener('cart:refresh', refreshCartAlumierFlag);

    // Email submission — modal form, legacy cart form, or new inline gate form
    document.addEventListener('submit', function (e) {
      var formId = e.target.id;
      if (formId !== 'alumier-verify-form-modal' &&
          formId !== 'alumier-verify-form' &&
          formId !== 'alumier-inline-form') return;
      e.preventDefault();
      var emailInput = e.target.querySelector('input[type="email"]');
      if (emailInput && emailInput.value) {
        verifyEmail(emailInput.value.trim(), function (allowed) {
          if (allowed) {
            // Keep the success message visible long enough for the customer
            // to read it before the gate auto-dismisses.
            setTimeout(function () {
              hideModal();
              removeInlineGate();
            }, 5000);
          }
        });
      }
    });

    // Modal close
    document.addEventListener('click', function (e) {
      if (e.target.id === 'alumier-modal-close') hideModal();
    });
  }

  // Public API
  window.AlumierVerify = {
    check: verifyEmail,
    isVerified: isVerified,
    getVerifiedEmail: function () { return verifiedEmail; },
    refreshCart: refreshCartAlumierFlag,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
